import React, { useState, useEffect, useReducer, useContext } from "react";
import { Link } from "react-router-dom";
import {
  IColumn,
  PrimaryButton,
  DetailsList,
  Selection,
  ActionButton,
  HoverCard,
  HoverCardType,
  Label,
  ScrollablePane,
  ScrollbarVisibility,
  MarqueeSelection,
  IDetailsHeaderProps,
  IRenderFunction,
  Sticky,
  StickyPositionType,
  Text,
  ConstrainMode,
  SelectionMode
} from "@fluentui/react";
import { ISLEntryGridItem } from "../schemas/SLEntryGridSchemas";
import { getFormattedDateString } from "../services/DateUtil";
import { ISLBatchPopupOverlay } from "./SLBatchPopup/SLBatchPopup";
import { getAttachmentDownloadHandler } from "./Attachments";

import { css } from "@emotion/css";

import { For } from "react-loops";
import { RuntimeError } from "../services/ErrorHelpers";
import { copyAndSort } from "../utilities/gridSorter";
import { RequireAtLeastOne } from "../services/TypeUtil";
import { IUserProviderState, UserContext } from "../contexts/UserContext";

const styles = css`
  font-size: inherit;
  font-weight: 500;
  color: var(--main-bg-color, gray);
  outline: currentcolor none medium;
  text-decoration: none;
`;

const buttonStyles = css`
  background-color: transparent;
  color: var(--main-bg-color, gray);
  font-size: 12px;
  font-weight: normal;
`;

const statusBoxStyle = css`
  width: 96px;
  list-style: none;
  padding: 0 16px;
`;

export interface IProcessingStatus {
  errors: number;
  successes: number;
  statuses: {
    [key: number]: {
      type: "error" | "success";
      messages: string[];
      nameDocumentId: string;
    };
  };
}

const ProcessingStatusMessages = (props: { messages: string[] }) => {
  return (
    <ul className={statusBoxStyle}>
      <For of={props.messages}>{(message) => <li>{message}</li>}</For>
    </ul>
  );
};

function SLEntriesDetailsProcessingStatus({
  item,
  processingStatus
}: {
  item: ISLEntryGridItem;
  processingStatus: IProcessingStatus["statuses"];
}) {
  if (item.slEntryId in processingStatus) {
    const status = processingStatus[item.slEntryId];
    if (status.type === "success") {
      return status.messages;
    } else {
      return (
        <HoverCard
          plainCardProps={{
            onRenderPlainCard: ProcessingStatusMessages,
            renderData: { messages: status.messages }
          }}
          instantOpenOnClick={true}
          type={HoverCardType.plain}
        >
          <Label style={{ color: "red" }}>Errors</Label>
        </HoverCard>
      );
    }
  }
  return null;
}

export interface ISLEntriesDetailsProps {
  routeState: () => { origin: string; state: { [key: string]: any } | undefined } | undefined;
  newTab?: boolean;
  processingStatus: IProcessingStatus["statuses"];
  items: ISLEntryGridItem[];
  selection: Selection;
  batchPopup: ISLBatchPopupOverlay["ref"] | null;
  height?: number;
  onPopupOpened?: () => void;
}

function SLEntriesDetailsAttachmentSelection({
  item,
  routeState,
  accessToken
}: {
  item: ISLEntryGridItem;
  routeState: ISLEntriesDetailsProps["routeState"];
  accessToken: IUserProviderState["accessToken"];
}) {
  if (item.attachmentCount !== undefined) {
    if (item.attachmentCount > 1) {
      return (
        <Link
          className={styles}
          to={{
            pathname: `/slcreate/${item.slEntryId}`,
            state: routeState()
          }}
        >
          Multiple
        </Link>
      );
    } else if (item.attachmentCount === 1) {
      return (
        <PrimaryButton
          className={buttonStyles}
          onClick={getAttachmentDownloadHandler(item.attachmentBlobName, item.attachmentFileName, accessToken)}
          text={item.attachmentFileName}
        />
      );
    }
  }
  return null;
}

interface IColumnOptions {
  props: ISLEntriesDetailsProps;
  columnClickHandler: (ev: any, column: IColumn) => void;
  isSortedIndex?: number;
  isSortedDescending: boolean;
  accessToken: IUserProviderState["accessToken"];
}

function GetGridDetailsColumns({
  props,
  columnClickHandler,
  isSortedIndex,
  isSortedDescending,
  accessToken
}: IColumnOptions): IColumn[] {
  const cols = [
    {
      key: "nameDocumentId",
      name: "Name / Document#",
      fieldName: "nameDocumentId",
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
      onColumnClick: columnClickHandler,
      onRender: (item: ISLEntryGridItem) => {
        const link = (type: "slbatch" | "slcreate", id: number | string, newTab: boolean) => {
          if (!newTab) {
            return (
              <Link
                className={styles}
                to={{
                  pathname: `/${type}/${id}`,
                  state: props.routeState()
                }}
              >
                {item.nameDocumentId}
              </Link>
            );
          } else {
            return (
              <a href={`/${type}/${id}`} target="_blank" rel="noopener noreferrer">
                {item.nameDocumentId}
              </a>
            );
          }
        };

        if (props.newTab) {
          // we are on an overlay, so it's always to slcreate
          return link("slcreate", item.slEntryId, props.newTab);
        }

        if (item.format === "Batch") {
          if (!item.refguid) {
            throw new RuntimeError("Batch item needs to have Refguid");
          }
          if ((item.status === "ValidationFailed" || item.status === "Submitted") && item.refguid) {
            return link("slbatch", item.refguid, false);
          }
        }
        return link("slcreate", item.slEntryId, false);
      }
    },
    {
      key: "companyCode",
      name: "Company Code",
      fieldName: "companyCode",
      minWidth: 100,
      maxWidth: 100,
      isResizable: true,
      onColumnClick: columnClickHandler
    },
    {
      key: "description",
      name: "Description",
      fieldName: "description",
      minWidth: 100,
      maxWidth: 180,
      isResizable: true,
      onColumnClick: columnClickHandler
    },
    {
      key: "status",
      name: "Status",
      fieldName: "status",
      minWidth: 60,
      maxWidth: 80,
      isResizable: true,
      onColumnClick: columnClickHandler
    },
    {
      key: "poster",
      name: "Posted By",
      fieldName: "poster",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: columnClickHandler
    },
    {
      key: "additionalPoster",
      name: "Poster(s)",
      fieldName: "additionalPoster",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: columnClickHandler
    },
    {
      key: "reviewers",
      name: "Reviewed By",
      fieldName: "reviewers",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: columnClickHandler
    },
    {
      key: "fiscalYearAndPeriod",
      name: "Fiscal Year/Period",
      fieldName: "fiscalYearAndPeriod",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: columnClickHandler
    },
    {
      key: "comments",
      name: "Comments",
      fieldName: "comments",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: columnClickHandler
    },
    {
      key: "modifiedDate",
      name: "Modified Date",
      fieldName: "modifiedDate",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: columnClickHandler,
      onRender: (item: ISLEntryGridItem) => getFormattedDateString(item.modifiedDate)
    },
    {
      key: "format",
      name: "Format",
      fieldName: "format",
      minWidth: 70,
      maxWidth: 90,
      isResizable: true,
      onColumnClick: columnClickHandler,
      onRender: (item: ISLEntryGridItem) => {
        if (!item.batchRefguid) {
          return item.format;
        }
        if (props.batchPopup === null) {
          return item.format;
        } else {
          return (
            <ActionButton
              style={{
                fontSize: "12px",
                border: "none",
                height: "auto"
              }}
              onClick={() => {
                if (props.onPopupOpened) {
                  props.onPopupOpened();
                }
                if (props.batchPopup) {
                  props.batchPopup.open(item.batchRefguid as string);
                }
              }}
            >
              Batch
            </ActionButton>
          );
        }
      }
    },
    {
      key: "attachments",
      name: "Attachments",
      fieldName: "attachmentFileName",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: columnClickHandler,
      onRender: (item: ISLEntryGridItem) =>
        SLEntriesDetailsAttachmentSelection({ item, routeState: props.routeState, accessToken })
    },
    {
      key: "processingStatus",
      name: "Processing Status",
      fieldName: "processingStatus",
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: columnClickHandler,
      onRender: (item: ISLEntryGridItem) =>
        SLEntriesDetailsProcessingStatus({ item, processingStatus: props.processingStatus })
    }
  ];
  if (isSortedIndex !== undefined) {
    (cols[isSortedIndex] as any).isSorted = true;
    (cols[isSortedIndex] as any).isSortedDescending = isSortedDescending;
  }

  return cols;
}

function columnNameToIndex(nameToLookFor: string) {
  const columnNames = [
    "nameDocumentId",
    "companyCode",
    "description",
    "status",
    "poster",
    "additionalPoster",
    "reviewers",
    "fiscalYearAndPeriod",
    "comments",
    "modifiedDate",
    "format",
    "attachmentFileName",
    "processingStatus"
  ];
  const idx = columnNames.indexOf(nameToLookFor);
  if (idx === -1) {
    throw new Error("column name does not match columns");
  }
  return idx;
}

const ColumnClickListenerConstructor =
  (
    gridItems: ISLEntryGridItem[],
    setDetailsState: React.Dispatch<
      RequireAtLeastOne<IDetailsState, "gridItems" | "isSortedIndex" | "isSortedDescending">
    >
  ) =>
  (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    if (column.fieldName === undefined) {
      throw new RuntimeError("Columns must have names.");
    }

    if (column.fieldName !== null || column.fieldName !== undefined) {
      const newItems = copyAndSort(gridItems, column.fieldName, !!!column.isSortedDescending);
      setDetailsState({
        gridItems: newItems,
        isSortedIndex: columnNameToIndex(column.fieldName),
        isSortedDescending: !!!column.isSortedDescending
      });
    }
  };

interface IDetailsState {
  gridItems: ISLEntryGridItem[];
  isSortedIndex?: number;
  isSortedDescending: boolean;
}

type IDetailsStateReducer = (state: IDetailsState, changes: RequireAtLeastOne<IDetailsState>) => IDetailsState;

const SLEntriesDetails = (props: ISLEntriesDetailsProps): JSX.Element => {
  const { accessToken } = useContext(UserContext);
  const [detailsState, setDetailsState] = useReducer<IDetailsStateReducer>(
    (previousState, changes) => ({
      ...previousState,
      ...changes
    }),
    {
      gridItems: props.items,
      isSortedIndex: undefined,
      isSortedDescending: false
    }
  );

  const [columns, setColumns] = useState(
    GetGridDetailsColumns({
      props,
      columnClickHandler: ColumnClickListenerConstructor(detailsState.gridItems, setDetailsState),
      isSortedIndex: detailsState.isSortedIndex,
      isSortedDescending: detailsState.isSortedDescending,
      accessToken
    })
  );

  useEffect(() => {
    setColumns(
      GetGridDetailsColumns({
        props,
        columnClickHandler: ColumnClickListenerConstructor(detailsState.gridItems, setDetailsState),
        isSortedIndex: detailsState.isSortedIndex,
        isSortedDescending: detailsState.isSortedDescending,
        accessToken
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, detailsState]);

  if (detailsState.gridItems.length === 0) {
    return (
      <Text
        variant="xLarge"
        style={{
          color: "var(--accent-font-color, gray)",
          textAlign: "center"
        }}
      >
        No Items to show.
      </Text>
    );
  }

  return (
    <div
      className={css`
        overflow: auto;
        max-height: 80vh;
        height: ${props.height || 460}px;
        position: relative;
      `}
      data-is-scrollable="true"
    >
      <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
        <MarqueeSelection selection={props.selection}>
          <DetailsList
            compact={false}
            columns={columns}
            getRowAriaLabel={(item) => {
              return `Entry ${item.nameDocumentId}`;
            }}
            onRenderDetailsHeader={(
              detailsHeaderProps?: IDetailsHeaderProps,
              defaultRender?: IRenderFunction<IDetailsHeaderProps>
            ) => {
              if (!detailsHeaderProps || !defaultRender) {
                return null;
              }

              return (
                <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced={true}>
                  {defaultRender({ ...detailsHeaderProps })}
                </Sticky>
              );
            }}
            constrainMode={ConstrainMode.unconstrained}
            items={detailsState.gridItems}
            ariaLabelForGrid={`SL Entries`}
            ariaLabelForSelectionColumn="Toggle SL Entries"
            ariaLabelForSelectAllCheckbox="Toggle all SL Entries"
            selectionMode={SelectionMode.multiple}
            selection={props.selection}
          />
        </MarqueeSelection>
      </ScrollablePane>
    </div>
  );
};

export default SLEntriesDetails;
